type LowercaseValues<Values extends string[]> = Lowercase<Values[number]>[];

export function createFeatureFlagConfig<Key extends string, DefaultValueType>(
  key: Key,
  values: boolean,
  defaultValue?: DefaultValueType,
): {
  key: string;
  queryParam: string;
  supportedValues: boolean;
  defaultValue: DefaultValueType;
  type: 'config';
};

export function createFeatureFlagConfig<Key extends string, Values extends string[]>(
  key: Key,
  values: readonly [...Values],
  defaultValue?: Values[number] | undefined,
): { key: string; queryParam: string; supportedValues: readonly [...LowercaseValues<Values>]; defaultValue: Values[number]; type: 'config' };

export function createFeatureFlagConfig<Key extends string, Values, DefaultValueType>(key: Lowercase<Key>, values: Values, defaultValue?: DefaultValueType) {
  return {
    key,
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: Array.isArray(values) ? values.map(val => val.toLowerCase()) : values,
    defaultValue,
    type: 'config',
  } as const;
}

export function createBooleanFeatureFlag<Key extends string>(key: Key, defaultValue?: boolean) {
  return {
    key,
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: [true, false],
    defaultValue,
    type: 'boolean',
  } as { key: string; queryParam: string; defaultValue: true | false; supportedValues: [true, false]; type: 'boolean' };
}

interface RemoteFeatureFlagConfig<TKey extends string, TValues, TDefaultValue> {
  key: TKey;
  queryParam: string;
  supportedValues: TValues;
  defaultValue: TDefaultValue;
  consentValueForTrue?: TValues extends ReadonlyArray<infer TValue> ? TValue : never;
  type: 'remoteconfig';
}

interface RemoteFeatureFlagParams<TKey extends string, TValues extends ReadonlyArray<string> = ['on'], TDefaultValue extends TValues[number] | undefined = undefined> {
  /**
   * The name of the feature flag. This must match (case insensitively) a feature flag or experiment defined in the remote config service.
   */
  key: TKey;

  /**
   * The possible defined values of the feature flag. This should match (case insensitively) the variants for the flag defined in the remote
   * config service. Any value for the feature flag returned from the remote config service which is not in this list will be ignored.
   *
   * Defaults to `['on']`, which is used for an on/off feature flag.
   */
  supportedValues?: readonly [...TValues];

  /**
   * The default value for the feature flag. This will be returned if the value of the feature flag is undefined, which can occur in the following cases:
   *
   *   - The feature flag value is being loaded from the remote config service.
   *   - The feature flag is not defined in the remote config service.
   *   - The feature flag is defined in the remote config service, but the user is not assigned to the feature flag.
   *   - The feature flag is defined in the remote config service, but the user is assigned to a variant which is not in the `supportedValues` list.
   *
   * Defaults to `undefined`.
   */
  defaultValue?: TDefaultValue;

  /**
   * Optional value to use when consent is explicitly granted.
   * This allows consent gating to map `true` to a specific variant.
   */
  consentValueForTrue?: TValues[number];
}

/**
 * Creates an on/off feature flag whose value is read from a remote config service, like Amplitude Experiment. The feature flag may be
 * statically assigned, or assigned based on an experiment.
 *
 * For more details on feature flagging and experimentation, see: https://withjoy.atlassian.net/wiki/spaces/KNOW/pages/2195718182
 *
 * @param key The name of the feature flag. This must match (case insensitively) a feature flag or experiment
 *           defined in the remote config service.
 */
export function createRemoteFeatureFlag<Key extends string>(key: Key): RemoteFeatureFlagConfig<Key, ['on'], undefined>;

/**
 * Creates a feature flag whose value is read from a remote config service, like Amplitude Experiment. The feature flag may be
 * statically assigned, or assigned based on an experiment.
 *
 * For more details on feature flagging and experimentation, see: https://withjoy.atlassian.net/wiki/spaces/KNOW/pages/2195718182
 */
export function createRemoteFeatureFlag<
  Key extends string,
  Values extends ReadonlyArray<string>,
  DefaultValue extends Values[number],
  ConsentValueForTrue extends Values[number] | undefined,
>({
  key,
  supportedValues,
  defaultValue,
  consentValueForTrue,
}: RemoteFeatureFlagParams<Key, Values, DefaultValue> & { consentValueForTrue?: ConsentValueForTrue }): RemoteFeatureFlagConfig<Key, Values, DefaultValue>;
export function createRemoteFeatureFlag(
  params: string | RemoteFeatureFlagParams<string, ReadonlyArray<string>, string>,
): RemoteFeatureFlagConfig<string, ReadonlyArray<string>, string | undefined> {
  const {
    key,
    supportedValues = ['on'],
    defaultValue,
    consentValueForTrue,
  }: RemoteFeatureFlagParams<string, ReadonlyArray<string>, string> = typeof params === 'string' ? { key: params } : params;

  return {
    key,
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: supportedValues.map(val => val.toLowerCase()),
    defaultValue,
    consentValueForTrue: consentValueForTrue?.toLowerCase(),
    type: 'remoteconfig',
  };
}
